import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flame, Check, Loader2, ArrowRight, Lightbulb } from "lucide-react";
import { TOPIC_MAP, LEVELS } from "@/lib/topics";
import { generateQuiz, generateScenario } from "@/lib/api";
import { useClient } from "@/context/ClientContext";

export default function Quiz() {
  const { topicId, difficulty, mode } = useParams();
  const navigate = useNavigate();
  const { recordResult, setLastResult, profile, clientId } = useClient();
  const topic = TOPIC_MAP[topicId];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    setIdx(0);
    setSelected(null);
    setLocked(false);
    try {
      if (mode === "scenario") {
        const n = 3;
        const results = await Promise.all(
          Array.from({ length: n }).map(() => generateScenario(topicId, difficulty, profile))
        );
        setItems(
          results.map((s) => ({
            title: s.title,
            context: s.context,
            question: s.question,
            options: s.options,
            correctIndex: s.correctIndex,
            explanation: s.explanation,
            emoji: s.emoji || "🧩",
          }))
        );
      } else {
        const count = LEVELS.find((l) => l.id === difficulty)?.questions || 5;
        const data = await generateQuiz(topicId, difficulty, count, profile);
        setItems(
          data.questions.map((q) => ({
            question: q.question,
            options: q.options,
            correctIndex: q.correctIndex,
            explanation: q.explanation,
            emoji: q.emoji || "💡",
          }))
        );
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, difficulty, mode]);

  const loadedKey = useRef("");
  useEffect(() => {
    const key = `${topicId}|${difficulty}|${mode}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;
    load();
  }, [topicId, difficulty, mode, load]);

  const current = items[idx];

  const handleAnswer = (optionIdx) => {
    if (locked) return;
    setSelected(optionIdx);
    setLocked(true);
    const isCorrect = optionIdx === current.correctIndex;
    if (isCorrect) {
      setCorrectCount((c) => c + 1);
      setStreak((s) => {
        const ns = s + 1;
        setMaxStreak((m) => Math.max(m, ns));
        return ns;
      });
    } else {
      setStreak(0);
    }
  };

  const next = () => {
    if (idx + 1 >= items.length) {
      const result = recordResult(topicId, difficulty, {
        correct: correctCount,
        total: items.length,
        streak: maxStreak,
        mode,
      });
      setLastResult({
        ...result,
        correct: correctCount,
        total: items.length,
        maxStreak,
        topicId,
        difficulty,
        mode,
      });
      navigate(`/clients/${clientId}/results`);
      return;
    }
    setIdx((i) => i + 1);
    setSelected(null);
    setLocked(false);
  };

  if (loading) return <Loader topic={topic} mode={mode} />;
  if (error)
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <p className="font-display text-2xl font-bold text-slate-700 mb-2">Oops!</p>
        <p className="text-slate-500 font-semibold mb-6">We couldn't load the challenge. Please try again.</p>
        <button
          data-testid="retry-btn"
          onClick={load}
          className="text-white font-bold rounded-2xl px-6 py-3 border-b-4"
          style={{ background: topic.color, borderColor: topic.deep }}
        >
          Retry
        </button>
      </div>
    );

  const progressPct = ((idx + (locked ? 1 : 0)) / items.length) * 100;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-5 pb-28">
      {/* top row: quit + progress + streak */}
      <div className="flex items-center gap-4 mb-6">
        <button
          data-testid="quit-btn"
          onClick={() => navigate(`/clients/${clientId}/topic/${topicId}`)}
          className="text-slate-300 hover:text-slate-600 transition-colors"
        >
          <X className="w-7 h-7" strokeWidth={2.5} />
        </button>
        <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            className="h-full rounded-full relative"
            style={{ background: topic.color }}
            animate={{ width: `${progressPct}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          >
            <div className="absolute inset-0 shimmer" />
          </motion.div>
        </div>
        <div
          data-testid="streak-counter"
          className={`flex items-center gap-1 font-extrabold text-sm ${streak > 0 ? "text-orange-500" : "text-slate-300"}`}
        >
          <Flame className={`w-5 h-5 ${streak > 0 ? "fill-orange-400 text-orange-500" : ""}`} />
          {streak}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
        >
          {mode === "scenario" && current.context && (
            <div className="rounded-3xl border-2 p-5 mb-4" style={{ background: topic.tint, borderColor: topic.tint }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{current.emoji}</span>
                <span className="font-display font-bold text-base" style={{ color: topic.deep }}>{current.title}</span>
              </div>
              <p className="text-slate-700 font-semibold leading-relaxed text-[15px]">{current.context}</p>
            </div>
          )}

          {/* Animated picture / emoji */}
          <div className="flex justify-center mb-4">
            <motion.div
              key={`emoji-${idx}`}
              initial={{ scale: 0, rotate: -25 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 12, delay: 0.05 }}
              className="w-24 h-24 rounded-3xl flex items-center justify-center border-b-4 shadow-sm"
              style={{ background: topic.tint, borderColor: topic.color }}
            >
              <motion.span
                className="text-5xl"
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
              >
                {current.emoji}
              </motion.span>
            </motion.div>
          </div>

          <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-800 mb-6 leading-snug text-center" data-testid="question-text">
            {current.question}
          </h2>

          <div className="space-y-3">
            {current.options.map((opt, i) => {
              const isCorrect = i === current.correctIndex;
              const isSelected = i === selected;
              let cls = "bg-white border-slate-200 text-slate-700";
              if (locked) {
                if (isCorrect) cls = "bg-emerald-50 border-emerald-400 text-emerald-800";
                else if (isSelected) cls = "bg-rose-50 border-rose-400 text-rose-800";
                else cls = "bg-white border-slate-100 text-slate-400";
              }
              return (
                <motion.button
                  key={i}
                  whileTap={!locked ? { scale: 0.98 } : {}}
                  disabled={locked}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => handleAnswer(i)}
                  className={`w-full text-left rounded-2xl border-2 border-b-4 px-5 py-4 font-bold transition-all flex items-center justify-between gap-3 ${cls} ${
                    !locked ? "active:border-b-2 active:translate-y-[2px] hover:border-slate-300" : ""
                  }`}
                >
                  <span className="text-[15px] leading-snug">{opt}</span>
                  {locked && isCorrect && <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={3} />}
                  {locked && isSelected && !isCorrect && <X className="w-5 h-5 text-rose-500 shrink-0" strokeWidth={3} />}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* explanation + continue (sticky footer) */}
      <AnimatePresence>
        {locked && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className={`fixed bottom-0 left-0 right-0 z-40 border-t-2 ${
              selected === current.correctIndex ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
            }`}
          >
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
              <div className="flex items-start gap-2 mb-3">
                <Lightbulb className={`w-5 h-5 mt-0.5 shrink-0 ${selected === current.correctIndex ? "text-emerald-600" : "text-rose-600"}`} />
                <div>
                  <p className={`font-display font-bold ${selected === current.correctIndex ? "text-emerald-700" : "text-rose-700"}`}>
                    {selected === current.correctIndex ? "Correct!" : "Not quite"}
                  </p>
                  <p className="text-slate-600 text-sm font-semibold leading-snug" data-testid="explanation-text">
                    {current.explanation}
                  </p>
                </div>
              </div>
              <button
                data-testid="continue-btn"
                onClick={next}
                className="w-full text-white font-bold rounded-2xl py-3.5 border-b-4 active:border-b-0 active:translate-y-[3px] transition-all flex items-center justify-center gap-2"
                style={{
                  background: selected === current.correctIndex ? "#34D399" : "#FB7185",
                  borderColor: selected === current.correctIndex ? "#059669" : "#E11D48",
                }}
              >
                {idx + 1 >= items.length ? "See results" : "Continue"}
                <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Loader = ({ topic, mode }) => (
  <div className="max-w-md mx-auto px-6 py-28 text-center">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
      className="inline-block mb-6"
    >
      <Loader2 className="w-12 h-12" style={{ color: topic?.color || "#29ABE2" }} strokeWidth={2.5} />
    </motion.div>
    <p className="font-display text-2xl font-bold text-[#005580] mb-1">
      Building your {mode === "scenario" ? "scenarios" : "quiz"}…
    </p>
    <p className="text-slate-400 font-semibold">Our AI coach is crafting fresh questions just for you.</p>
  </div>
);
